import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { getNextMondayMidnight, formatCountdown } from '../utils/countdownUtils';

const GENERATING_MESSAGE = 'Generando';

const OptimizationsCountdownBanner = ({
  hasOptimizations,
  hasAnyObjectives,
  hasAnyAccountConnected = true,
  onCreateObjectivesClick,
  onConnectAccountClick,
  isGenerating = false,
  mustWaitForNextUpdate = false,
  isLoading = false,
  /** Cuando el usuario filtra por una plataforma que no tiene conectada: { platform: 'meta'|'google', label: string, onConnect: fn } */
  connectPlatformPrompt = null,
}) => {
  const [countdown, setCountdown] = useState(() => {
    const target = getNextMondayMidnight();
    return formatCountdown(target);
  });

  useEffect(() => {
    const target = getNextMondayMidnight();

    const updateCountdown = () => {
      const result = formatCountdown(target);
      setCountdown(result);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60 * 1000); // Actualizar cada minuto
    return () => clearInterval(interval);
  }, []);

  // Estado de carga: mostrar botón en loading hasta que tengamos los datos definidos
  if (isLoading) {
    return (
      <div className="mb-4 flex justify-center">
        <div className="inline-flex items-center gap-3 backdrop-blur-xl bg-gradient-to-r from-fuchsia-500/20 via-purple-500/20 to-blue-500/20 border border-fuchsia-400/30 rounded-full px-5 py-2.5 shadow-[0_4px_14px_-2px_rgba(217,70,239,0.3),0_4px_20px_-4px_rgba(99,102,241,0.4)] opacity-70">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-fuchsia-300 border-t-transparent" />
          <span className="text-white/70 text-sm font-medium whitespace-nowrap">
            Cargando...
          </span>
        </div>
      </div>
    );
  }

  // Si no tiene cuentas conectadas, no objetivos, no optimizaciones: mensaje para conectar
  if (
    hasAnyAccountConnected === false &&
    hasAnyObjectives === false &&
    hasOptimizations === false &&
    typeof onConnectAccountClick === 'function'
  ) {
    return (
      <div className="mb-4 flex justify-center">
        <button
          type="button"
          onClick={onConnectAccountClick}
          className="inline-flex items-center gap-3 backdrop-blur-xl bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-600/20 border border-amber-400/30 rounded-full px-5 py-2.5 shadow-[0_4px_14px_-2px_rgba(245,158,11,0.3),0_4px_20px_-4px_rgba(234,179,8,0.4)] hover:border-amber-400/50 transition-all duration-300"
        >
          <span className="text-white/90 text-sm font-medium whitespace-nowrap">
            Conecta una cuenta publicitaria para desbloquear la APP
          </span>
        </button>
      </div>
    );
  }

  // Si no tiene objetivos en ninguna marca: botón turquesa para crear objetivos
  if (hasAnyObjectives === false && typeof onCreateObjectivesClick === 'function') {
    return (
      <div className="mb-4 flex justify-center">
        <button
          type="button"
          onClick={onCreateObjectivesClick}
          className="inline-flex items-center gap-3 backdrop-blur-xl bg-gradient-to-r from-cyan-500/20 via-teal-500/20 to-cyan-600/20 border border-cyan-400/30 rounded-full px-5 py-2.5 shadow-[0_4px_14px_-2px_rgba(6,182,212,0.3),0_4px_20px_-4px_rgba(20,184,166,0.4)] hover:border-cyan-400/50 transition-all duration-300"
        >
          <span className="text-white/90 text-sm font-medium whitespace-nowrap">
            Creá tus objetivos para generar las optimizaciones
          </span>
        </button>
      </div>
    );
  }

  // Si no hay optimizaciones (pero sí tiene objetivos)
  if (hasOptimizations === false) {
    // Usuario filtra por plataforma que no tiene conectada: mostrar botón para conectar
    if (connectPlatformPrompt && typeof connectPlatformPrompt.onConnect === 'function') {
      return (
        <div className="mb-4 flex justify-center">
          <button
            type="button"
            onClick={connectPlatformPrompt.onConnect}
            className="inline-flex items-center gap-3 backdrop-blur-xl bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-600/20 border border-amber-400/30 rounded-full px-5 py-2.5 shadow-[0_4px_14px_-2px_rgba(245,158,11,0.3),0_4px_20px_-4px_rgba(234,179,8,0.4)] hover:border-amber-400/50 transition-all duration-300"
          >
            <span className="text-white/90 text-sm font-medium whitespace-nowrap">
              Conecta {connectPlatformPrompt.label} para generar optimizaciones
            </span>
          </button>
        </div>
      );
    }
    // mustWait: usuario intentó generar, no hubo datos suficientes -> mostrar mensaje + countdown
    if (mustWaitForNextUpdate) {
      const countdownText =
        countdown.days > 0
          ? `${countdown.days}d ${countdown.hours}h ${countdown.minutes}m`
          : `${countdown.hours}h ${countdown.minutes}m`;
      return (
        <div className="mb-4 flex justify-center">
          <div className="inline-flex flex-col items-center backdrop-blur-xl bg-gradient-to-r from-slate-500/20 via-indigo-500/20 to-slate-600/20 border border-white/20 rounded-2xl px-5 py-3 shadow-lg max-w-xl text-center">
            <p className="text-white/90 text-sm font-medium leading-relaxed">
              Estamos buscando optimizaciones para tus anuncios. Te avisaremos cuando estén listas.
            </p>
            <p className="text-white/55 text-xs mt-2">
              Próxima ventana habitual de actualización:{' '}
              <span className="font-semibold text-white/75">{countdownText}</span>
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="mb-4 flex justify-center">
        <div
          role="status"
          aria-live="polite"
          aria-busy={isGenerating}
          className="inline-flex flex-col items-center backdrop-blur-xl bg-gradient-to-r from-slate-500/20 via-indigo-500/20 to-slate-600/20 border border-white/20 rounded-2xl px-5 py-3 shadow-lg max-w-xl text-center"
        >
          <p className="text-white/90 text-sm font-medium leading-relaxed">
            Estamos buscando optimizaciones para tus anuncios. Te avisaremos cuando estén listas.
          </p>
          {isGenerating ? (
            <p className="text-white/60 text-xs mt-2">{GENERATING_MESSAGE}…</p>
          ) : null}
        </div>
      </div>
    );
  }

  if (countdown.isComplete) {
    return null;
  }

  return (
    <div className="mb-4 flex justify-center">
      <div className="inline-flex flex-col items-center backdrop-blur-xl bg-gradient-to-r from-fuchsia-500/20 via-purple-500/20 to-blue-500/20 border border-fuchsia-400/30 rounded-full px-5 py-2.5 shadow-[0_4px_14px_-2px_rgba(217,70,239,0.3),0_4px_20px_-4px_rgba(99,102,241,0.4)] hover:border-fuchsia-400/50 transition-all duration-300">
        <div className="flex items-center gap-3">
          <Clock size={18} className="text-fuchsia-300 shrink-0" />
          <p className="text-white/90 text-sm font-medium whitespace-nowrap">
            Nuevas optimizaciones se generarán automáticamente
          </p>
        </div>
      </div>
    </div>
  );
};

export default OptimizationsCountdownBanner;
